import { Fragment } from "react";
import { useSelector } from "react-redux";
import useCarreraType from "../../hooks/useCarreraType";
import { selectMobileFeatures } from "../../reduxSlices/globalFlagsSlice";
import { selectStudent } from "../../reduxSlices/studentSlice";
import { InfoFechas, IOfferedSubjectSection } from "../../types";
import { findProfeTitular, formatDateToDDMMYYYY, parseHours } from "../../utils";
import { abbreviateTranslatedDay } from "../../utils/calendarHelpers";
import { transformHorariosData } from "../../utils/professorAndSubject";
import { useTranslatedTextState } from "../../contexts/TranslationContext";
import { CgMoreO } from "react-icons/cg";
import "./ProfessorAndSubjectHours.css";
import {
  getPrioritizedData,
  getProfesoresByRol,
  isNombreADesignar,
  PROFESOR_ROLE_GROUPS,
  ProfesorDetalle,
} from "./ProfessorAndSubjectHoursHelpers";
import useMediumScreen from "../../hooks/useMediumScreen";
import Translatable from "../Translatable/Translatable";

interface Props {
  subjectData: IOfferedSubjectSection["subjectData"];
  itemClass?: string;
  titleClass?: string;
  showBorderTop?: boolean;
  showRol?: boolean;
  allProfesVisible?: boolean;
  isDrawerContent: boolean;
  turno?: string;
  creditos?: number;
  infoFechas?: InfoFechas;
  modalidad?: string;
}

function NombreODesignar({ nombre }: { nombre?: string | string[] | null }) {
  if (isNombreADesignar(nombre)) {
    return <Translatable as="span" capitalize>A designar</Translatable>;
  }
  return <>{nombre}</>;
}

function ProfesorRoleGroups({
  detalles,
  titleClass,
}: {
  detalles: ProfesorDetalle[];
  titleClass: string;
}) {
  return (
    <>
      {PROFESOR_ROLE_GROUPS.map(({ rol, titulo }, index) => {
        const profesores = getProfesoresByRol(detalles, rol);
        if (profesores.length === 0) return null;

        return (
          <Fragment key={rol}>
            <h4 className={`${titleClass}${index > 0 ? " mt-2" : ""}`}>
              <Translatable as="span" capitalize>
                {titulo}
              </Translatable>
            </h4>
            {profesores.map((persona, i) => (
              <p key={`${rol}-${persona.nombre}-${i}`}>
                <NombreODesignar nombre={persona.nombre} />
              </p>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

function MissingInfoWarning() {
  return (
    <p className="font-semibold text-[#B7B7B7]">
      ⚠ <Translatable as="span">Información faltante.</Translatable>
    </p>
  );
}

function AulaTexto({ aula, mostrarAula }: { aula?: string; mostrarAula: boolean }) {
  const texto = mostrarAula ? aula : undefined;
  return texto ? <>{texto}</> : <Translatable as="span" capitalize>Sin asignar</Translatable>;
}

function DiaAbreviado({ dia }: { dia?: string }) {
  const { value, isTranslating } = useTranslatedTextState(dia ?? "", { capitalize: true });
  if (!dia) return null;

  const classes = ["translatable", isTranslating ? "translatable--pending" : ""].filter(Boolean).join(" ");
  return (
    <span data-translate className={classes}>
      {abbreviateTranslatedDay(value, dia)}
    </span>
  );
}

function HorarioRango({ dia, desde, hasta }: { dia?: string; desde: number; hasta: number }) {
  return (
    <>
      {dia ? <DiaAbreviado dia={dia} /> : null}
      {dia ? " " : ""}
      {parseHours(desde)}h a {parseHours(hasta)}h
    </>
  );
}

function ProfessorAndSubjectHours({
  subjectData,
  showBorderTop = false,
  isDrawerContent,
  turno,
  creditos,
  infoFechas,
  modalidad,
}: Props) {
  const student = useSelector(selectStudent);
  const mobileFeatures = useSelector(selectMobileFeatures);
  const { esCarreraDeGrado } = useCarreraType();
  const isMediumScreen = useMediumScreen();
  const horariosData: any[] = subjectData.subjectsByType ? transformHorariosData(subjectData.subjectsByType) : [];
  const profesorTipo: { detalles?: ProfesorDetalle[] } | undefined = horariosData.find(
    (tipo) => tipo?.titulo === "Profesor"
  );
  const horariosTipos = horariosData.filter((tipo) => tipo?.titulo !== "Profesor");

  const getFormattedDateRange = (mobile: boolean) => {
    const start = formatDateToDDMMYYYY(infoFechas?.fechaComienzo || "");
    const end = formatDateToDDMMYYYY(infoFechas?.fechaFin || "");

    if (start && end) {
      if (mobile) {
        return (
          <>
            {start} - {end}
          </>
        );
      } else {
        return (
          <>
            {start} -<br />
            {end}
          </>
        );
      }
    } else if (start) {
      return `Desde ${start}`;
    } else if (end) {
      return `Hasta ${end}`;
    }
    return "";
  };

  const addDatesToTransformedData = (transformedData: any[]) => {
    const dateRange = getFormattedDateRange(mobileFeatures);
    if (!esCarreraDeGrado && dateRange && !isDrawerContent) {
      if (!transformedData.some((item) => item.titulo === "Fechas")) {
        return [
          ...transformedData,
          {
            titulo: "Fechas",
            value: dateRange,
            detalles: [],
          },
        ];
      }
    }
    return transformedData;
  };

  const renderHorariosDrawer = (variant: "mobile" | "desktop") =>
    horariosTipos.map((tipo: any, index: number) => {
      if (tipo?.titulo === null) {
        return <MissingInfoWarning key={`warning-${index}`} />;
      }

      if (!student.mostrarHorarios) return null;

      if (variant === "mobile") {
        return (
          <div key={`${tipo?.titulo}-${index}`} className="grid grid-cols-7 text-[14px]">
            <h4 className="col-span-4 text-[#B7B7B7] text-[16px] pb-1">
              <Translatable as="span" capitalize>
                {tipo?.titulo}
              </Translatable>
            </h4>
            <h4 className="text-[#B7B7B7] text-[16px] pb-1 col-span-3">
              <Translatable as="span" capitalize>
                Aula
              </Translatable>
            </h4>
            {tipo?.detalles.map((horario: any, ind: number) => (
              <Fragment key={`${tipo?.titulo}-${horario?.dia}-${horario?.desde}-${ind}`}>
                <p className="col-span-4 pt-1">
                  {horario?.dia && (
                    <Translatable as="span" capitalize>
                      {horario.dia}
                    </Translatable>
                  )}{" "}
                  {parseHours(horario.desde)}h a {parseHours(horario.hasta)}h
                </p>
                <p className="pt-1 truncate col-span-3">
                  <AulaTexto aula={horario.aula} mostrarAula={student.mostrarAulas} />
                </p>
              </Fragment>
            ))}
          </div>
        );
      }

      return (
        <div className={`ph-horarios-inner-col ph-horarios-inner-col-mobile`} key={`${tipo?.titulo}-${index}`}>
          <div style={{ display: "flex" }}>
            <h4 className="flex-grow">
              <Translatable as="span" capitalize>
                {tipo?.titulo}
              </Translatable>
            </h4>
            <h4 className="w-[90px] max-w-[90px]">
              <Translatable as="span" capitalize>
                Aula
              </Translatable>
            </h4>
          </div>
          {tipo?.detalles.map((horario: any, ind: number) => (
            <div className="flex gap-1" key={`${tipo?.titulo}-${horario?.dia}-${horario?.desde}-${ind}`}>
              <p className="text-[14px] flex-grow text-nowrap">
                <HorarioRango dia={horario?.dia} desde={horario.desde} hasta={horario.hasta} />
              </p>
              <p
                title={student.mostrarAulas ? horario.aula : undefined}
                className="text-[14px] w-[90px] max-w-[90px] truncate"
              >
                <AulaTexto aula={horario.aula} mostrarAula={student.mostrarAulas} />
              </p>
            </div>
          ))}
        </div>
      );
    });

  if (isDrawerContent) {
    return (
      <>
        {mobileFeatures ? (
          <div className={`py-2 space-y-2 border-b-[1px] border-[#eeeeee]`}>
            <div className="space-y-1">{renderHorariosDrawer("mobile")}</div>
            <div>
              <div className="bg-[#eeeeee] h-[1px] my-2"></div>
              {profesorTipo && (
                <ProfesorRoleGroups detalles={profesorTipo.detalles ?? []} titleClass="text-[16px] text-[#B7B7B7]" />
              )}
            </div>
          </div>
        ) : (
          <div className={`content-rows-ph row-ph`} id="horarios-in-panel-drawer">
            <div className="col-horarios">{renderHorariosDrawer("desktop")}</div>
            <div className="w-[1px] bg-[#eeeeee]"></div>
            <div className="col-profesores">
              {profesorTipo && (
                <div className={`ph-horarios-inner-col ph-horarios-inner-col-mobile`}>
                  <ProfesorRoleGroups detalles={profesorTipo.detalles ?? []} titleClass="panel-drawer-h4-title" />
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  const renderMobileContent = () => {
    return (
      <div style={{ zIndex: 1 }} className={`w-[75%] space-y-2 h-full py-3`}>
        {horariosData.map((tipo: any, index: number) => {
          const renderHorarios = tipo?.titulo !== "Profesor";
          if (tipo?.titulo === null) {
            return <MissingInfoWarning key={`warning-${index}`} />;
          }

          if (!renderHorarios || !student.mostrarHorarios || index > 2) return null;

          const detalles = tipo?.detalles.slice(0, index === 2 ? 1 : 2);
          return (
            <div className={`text-[14px] flex`} key={`horario-${index}`}>
              <h4 className={`text-[#B7B7B7] w-[35%]`}>
                <Translatable as="span" capitalize>
                  {tipo?.titulo}
                </Translatable>
              </h4>
              <div className="w-[65%]">
                {detalles.map((horario: any, ind: number) => (
                  <p key={`detalle-${index}-${ind}`} className="flex items-center gap-1">
                    <HorarioRango dia={horario?.dia} desde={horario.desde} hasta={horario.hasta} />
                    {ind === 1 && tipo?.detalles.length > 2 ? <CgMoreO /> : null}
                    {index === 2 && (horariosData.length > 3 || tipo.detalles.length > 1) ? <CgMoreO /> : null}
                  </p>
                ))}
              </div>
            </div>
          );
        })}

        {!esCarreraDeGrado && infoFechas && getFormattedDateRange(mobileFeatures) && (
          <div className={`text-[14px] flex`}>
            <h4 className={`text-[#B7B7B7] w-[35%]`}>
              <Translatable as="span" capitalize>
                Fechas
              </Translatable>
            </h4>
            <div className="w-[65%]">
              <p>{getFormattedDateRange(mobileFeatures)}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {mobileFeatures ? (
        renderMobileContent()
      ) : (
        <div
          style={{ zIndex: 1 }}
          className={`content-rows-ph mobile-horarios row-ph ${showBorderTop ? "border-top-ph" : ""}`}
        >
          {subjectData.subjectsByType &&
            (() => {
              const transformedData = addDatesToTransformedData(horariosData);
              const { items } = getPrioritizedData(
                transformedData,
                turno,
                creditos,
                esCarreraDeGrado,
                modalidad,
                isMediumScreen
              );

              return items.map((tipo: any, index: number) => {
              if (tipo?.titulo === null) {
                return <MissingInfoWarning key={`warning-${index}`} />;
              }

              const columnClass = `ph-horarios-inner-col col-start-${tipo.columna}`;

              if (tipo?.isProfesor) {
                return (
                  <div className={columnClass} key={`profesor-${index}`}>
                    <h4 className={`ph-h4-title`}>
                      <Translatable as="span" capitalize>
                        Profesor/a
                      </Translatable>
                    </h4>
                    <p>
                      <NombreODesignar nombre={findProfeTitular(subjectData)} />
                    </p>
                  </div>
                );
              }

              if (
                tipo?.titulo === "Turno" ||
                tipo?.titulo === "Modalidad" ||
                tipo?.titulo === "Fechas" ||
                (tipo?.titulo === "Créditos" && tipo?.value)
              ) {
                return (
                  <div className={columnClass} key={`info-${index}`}>
                    <h4 className={`ph-h4-title`}>
                      <Translatable as="span" capitalize>
                        {tipo?.titulo}
                      </Translatable>
                    </h4>
                    <p>
                      {tipo?.titulo === "Modalidad" || tipo?.titulo === "Turno" ? (
                        <Translatable as="span">{String(tipo?.value)}</Translatable>
                      ) : (
                        tipo?.value
                      )}
                    </p>
                  </div>
                );
              }

              if (student.mostrarHorarios && tipo.value !== null) {
                return (
                  <div className={columnClass} key={`horario-${index}`}>
                    <h4 className={`ph-h4-title`}>
                      <Translatable as="span" capitalize>
                        {tipo?.titulo}
                      </Translatable>
                    </h4>
                    <div className="horarios-col-mobile">
                      {tipo?.detalles.slice(0, 2).map((horario: any, ind: number) => (
                        <p
                          className="text-nowrap truncate overflow-ellipsis flex items-center gap-1"
                          key={`detalle-${index}-${ind}`}
                        >
                          <HorarioRango dia={horario?.dia} desde={horario.desde} hasta={horario.hasta} />
                          {ind === 1 && tipo?.hasMore ? <CgMoreO /> : null}
                          {ind + 1 === tipo.detalles.length &&
                          tipo?.titulo?.toLowerCase?.().includes("práctica") &&
                          tipo.hasMore ? (
                            <CgMoreO />
                          ) : null}
                        </p>
                      ))}
                    </div>
                  </div>
                );
              }

              return null;
            });
          })()}
        </div>
      )}
    </>
  );
}

export default ProfessorAndSubjectHours;
